import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Colors,
  Icons,
  Typography,
  createClasses,
  spacing,
} from '@kp/react-ui';

import type { IndoorClimateScores } from '../../types/home-types';

import { KpiCard, type KpiCardProps } from './KpiCard';
import { KpiCardHeader } from './KpiCardHeader';

// Badge tone at 10 % alpha, matching the library's own badge fill.
const CHIP_BACKGROUND_ALPHA = '1A';

/** API letter grades → label keys; any other grade renders as-is. */
const SCORE_LABEL_KEY: Record<string, string> = {
  A: 'home.indoorClimate.score.good',
  B: 'home.indoorClimate.score.moderate',
  C: 'home.indoorClimate.score.poor',
};

const classes = createClasses({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing('xs'),
  },
  reading: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xs'),
  },
  value: {
    color: Colors.Neutral.text,
  },
  unit: {
    color: Colors.Neutral.textInactive,
  },
});

export interface IndoorClimateCardProps
  extends Pick<
    KpiCardProps,
    'href' | 'isLoading' | 'isError' | 'isEmpty' | 'onRetry' | 'className'
  > {
  scores: IndoorClimateScores;
}

export const IndoorClimateCard: React.FC<IndoorClimateCardProps> = ({
  scores,
  ...cardProps
}) => {
  const { t, i18n } = useTranslation();
  const title = t('home.indoorClimate.title');

  const rows: {
    key: keyof IndoorClimateScores;
    label: string;
    decimals: number;
  }[] = [
    {
      key: 'temperature',
      label: t('home.indoorClimate.temperature'),
      decimals: 1,
    },
    { key: 'co2', label: t('home.indoorClimate.co2'), decimals: 0 },
    { key: 'humidity', label: t('home.indoorClimate.humidity'), decimals: 0 },
  ];

  return (
    <KpiCard
      header={
        <KpiCardHeader
          icon={Icons.IconThermostat}
          title={title}
          subtitle={t('home.indoorClimate.subtitle')}
        />
      }
      linkLabel={t('home.openIn', { section: title })}
      {...cardProps}
    >
      {rows.map(({ key, label, decimals }) => {
        const score = scores[key];
        return (
          <div key={key} className={classes.row}>
            <Typography variant="labelSmall">{label}</Typography>
            <div className={classes.reading}>
              {score?.average != null && (
                <Typography variant="labelSmall" className={classes.value}>
                  {score.average.toLocaleString(i18n.language, {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                  })}
                </Typography>
              )}
              {score?.average != null && score.unit && (
                <Typography variant="labelSmall" className={classes.unit}>
                  {score.unit}
                </Typography>
              )}
              {score ? (
                <Badge
                  size="tiny"
                  // Runtime API colour — the one case inline style is allowed.
                  style={{
                    color: score.hexColor,
                    backgroundColor: `${score.hexColor}${CHIP_BACKGROUND_ALPHA}`,
                  }}
                >
                  <Badge.Title>
                    {t(SCORE_LABEL_KEY[score.name] ?? score.name)}
                  </Badge.Title>
                </Badge>
              ) : (
                <Badge size="tiny" color="grey">
                  <Badge.Title>{t('home.indoorClimate.noScore')}</Badge.Title>
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </KpiCard>
  );
};
